import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FaBars, FaTimes, FaPhone, FaEnvelope } from "react-icons/fa";

const HeaderContainer = styled(motion.header)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${(props) =>
    props.scrolled ? "rgba(10, 10, 10, 0.95)" : "rgba(10, 10, 10, 0.8)"};
  backdrop-filter: blur(20px);
  border-bottom: ${(props) =>
    props.scrolled ? "1px solid rgba(255, 255, 255, 0.1)" : "none"};
  transition: all 0.3s ease;
`;

const TopBar = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 8px 0;
  font-size: 14px;
  text-align: center;
  color: white;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Nav = styled.nav`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;

  @media (max-width: 768px) {
    padding: 0 20px;
    height: 70px;
  }
`;

const Logo = styled(motion.div)`
  font-size: 28px;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const LogoText = styled.span`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const LogoIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 18px;

  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    font-size: 16px;
  }
`;

const NavMenu = styled(motion.ul)`
  display: flex;
  list-style: none;
  align-items: center;
  gap: 50px;

  @media (max-width: 1024px) {
    gap: 30px;
  }

  @media (max-width: 768px) {
    position: fixed;
    top: 70px;
    left: ${(props) => (props.isOpen ? "0" : "-100%")};
    width: 100%;
    height: calc(100vh - 70px);
    background: rgba(10, 10, 10, 0.98);
    backdrop-filter: blur(20px);
    flex-direction: column;
    justify-content: flex-start;
    padding-top: 50px;
    transition: left 0.3s ease;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const NavItem = styled.li`
  position: relative;

  @media (max-width: 768px) {
    margin: 15px 0;
  }
`;

const NavLink = styled(motion.a)`
  color: #ffffff;
  font-weight: 500;
  font-size: 16px;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }

  &:hover {
    color: #667eea;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    padding: 10px 0;
  }
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 28px;
  border-radius: 30px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(102, 126, 234, 0.4);
  }

  @media (max-width: 768px) {
    margin-top: 20px;
    padding: 15px 35px;
    font-size: 16px;
  }
`;

const MobileMenuButton = styled(motion.button)`
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  color: #ffffff;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  font-size: 14px;
  color: #ffffff;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  transition: color 0.3s ease;

  &:hover {
    color: #667eea;
  }
`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMenuOpen(false);
  };

  return (
    <HeaderContainer
      scrolled={isScrolled}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <TopBar>
        深圳二三三数字科技有限公司 | 专业广告投放服务 | 咨询热线：400-888-9999
      </TopBar>

      <Nav>
        <Logo
          onClick={() => scrollToSection("hero")}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <LogoIcon>233</LogoIcon>
          <LogoText>深圳二三三数字科技</LogoText>
        </Logo>

        <NavMenu
          isOpen={isMenuOpen}
          initial={false}
          animate={isMenuOpen ? { opacity: 1 } : { opacity: 1 }}
        >
          <NavItem>
            <NavLink
              onClick={() => scrollToSection("hero")}
              whileHover={{ scale: 1.05 }}
            >
              首页
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              onClick={() => scrollToSection("services")}
              whileHover={{ scale: 1.05 }}
            >
              服务
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              onClick={() => scrollToSection("about")}
              whileHover={{ scale: 1.05 }}
            >
              关于我们
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              onClick={() => scrollToSection("portfolio")}
              whileHover={{ scale: 1.05 }}
            >
              案例
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              onClick={() => scrollToSection("team")}
              whileHover={{ scale: 1.05 }}
            >
              团队
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              onClick={() => scrollToSection("contact")}
              whileHover={{ scale: 1.05 }}
            >
              联系我们
            </NavLink>
          </NavItem>
          <NavItem>
            <CTAButton
              onClick={() => scrollToSection("contact")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              免费咨询
            </CTAButton>
          </NavItem>
        </NavMenu>

        <ContactInfo>
          <ContactItem>
            <FaPhone />
            <span>400-888-9999</span>
          </ContactItem>
          <ContactItem>
            <FaEnvelope />
            <span>kun@ersansan.cn</span>
          </ContactItem>
        </ContactInfo>

        <MobileMenuButton
          onClick={toggleMenu}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <FaTimes /> : <FaBars />}
        </MobileMenuButton>
      </Nav>
    </HeaderContainer>
  );
};

export default Header;
